import React, { useRef, useLayoutEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { Menu } from "primereact/menu";
import { useReactToPrint } from "react-to-print";
import "./style.css";

const QRToPrint = React.forwardRef(({ shopName, chatUrl }, ref) => {
  useLayoutEffect(() => {
    // Add print styles to document head
    const style = document.createElement("style");
    style.innerHTML = `
      .brand-name{
        font-size: 60px;
        font-height: 30px;
        font-weight: 700;
        text-align: center;
        color: #FFFFFF;
        content: '${process.env.REACT_APP_BRAND_NAME}';
      }
      .context{
        padding: 40px;
        font-size: 30px;
        font-height: 30px;
        font-weight: 600;
        text-align: center;
        color: #FFFFFF;
        content: 'Scan to message us and discover more!';
      }
      .shop-name{
        padding: 40px 0 20px;
        font-size: 30px;
        font-height: 30px;
        content: '${shopName}';
        text-align: center;
        width: 100%;
        margin: 0 auto;
      }
      .business-info strong{
        content: '${shopName}';
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, [shopName]);

  return (
    <div ref={ref} className="print-container print-only">
      <div className="qr-container">
        <h1 className="shop-name">{shopName}</h1>
        <QRCodeSVG
          value={chatUrl}
          size={300}
          title={`Chat with ${shopName} - Powered by ${process.env.REACT_APP_BRAND_NAME}`}
          level="L"
        />
        <div className="business-info" style={{ marginTop: '15px' }}>
          <strong>{shopName}</strong>
        </div>
      </div>
      <p className="context">Scan to message us and discover more!</p>
    </div>
  );
});

const QRPrintModal = ({
  shopName: propShopName,
  chatUrl: propChatUrl,
  userRecord = {},
}) => {
  const toast = React.useRef(null);
  const menu = React.useRef(null);
  const contentRef = useRef();

  // Use props if provided, otherwise use empty values
  const shopName = (propShopName || userRecord?.shopName || "").trim();
  const chatUrl = shopName 
    ? `${window.location.origin}/${encodeURIComponent(shopName.replace(/[^a-z0-9\s]/gi, '').replace(/\s+/g, ' ').trim())}`
    : window.location.origin;

  const handlePrint = useReactToPrint({ contentRef });

  const handleShare = async (platform) => {
    if (!chatUrl) return;

    const shareText = shopName
      ? `Chat with ${shopName} directly!`
      : "Chat with me directly!";
    const encodedUrl = encodeURIComponent(chatUrl);
    const encodedText = encodeURIComponent(shareText);

    const shareUrls = {
      whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
      telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
    };

    if (platform === "copy") {
      navigator.clipboard.writeText(chatUrl);
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Chat link copied to clipboard!",
        life: 3000,
      });
      return;
    }

    if (platform === "print") {
      handlePrint();
      return;
    }

    window.open(shareUrls[platform], "_blank");
  };

  const shareItems = [
    {
      label: "Copy Link",
      icon: "pi pi-copy",
      command: () => handleShare("copy"),
    },
    {
      label: "Print QR Code",
      icon: "pi pi-print",
      command: () => handleShare("print"),
    },
  ];

  return (
    <>
      <div className="flex flex-column align-items-center">
        <div className="text-center mb-4">
          <p className="text-600 mb-4">
            Share your QR code or link to receive messages from customers.
          </p>

          <div className="flex justify-content-center mb-4">
            <div
              className="p-4 border-round"
              style={{
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                background: "var(--primary-500)",
              }}
            >
              {chatUrl && (
                <QRCodeSVG
                  value={chatUrl}
                  size={200}
                  level="L"
                  includeMargin={true}
                  title={`Chat with ${shopName} - Powered by ${process.env.REACT_APP_BRAND_NAME}`}
                />
              )}
            </div>
          </div>

          <div className="flex flex-column align-items-center gap-3 mb-4">
            <span className="text-600">Your Chat Link:</span>
            <div className="flex w-full gap-2">
              <InputText value={chatUrl || ""} readOnly className="w-full" />
              <Button
                icon="pi pi-share-alt"
                severity="primary"
                onClick={(e) => menu.current.toggle(e)}
                className="p-button-raised"
              />
            </div>
          </div>
        </div>

        <div className="w-full">
          <Divider align="center" className="mb-4">
            <span className="text-600">How it works</span>
          </Divider>

          <div className="grid">
            <div className="col-12 md:col-6">
              <div className="flex align-items-center gap-3 mb-3">
                <i className="pi pi-share-alt text-primary-500 text-xl"></i>
                <div>
                  <h4 className="mb-1">Share Your Link</h4>
                  <p className="text-600 m-0">Share your QR code or link with potential customers</p>
                </div>
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="flex align-items-center gap-3 mb-3">
                <i className="pi pi-comments text-primary-500 text-xl"></i>
                <div>
                  <h4 className="mb-1">Start Chatting</h4>
                  <p className="text-600 m-0">They can scan or click to start a conversation with you</p>
                </div>
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="flex align-items-center gap-3 mb-3">
                <i className="pi pi-inbox text-primary-500 text-xl"></i>
                <div>
                  <h4 className="mb-1">Manage Messages</h4>
                  <p className="text-600 m-0">All messages will appear in your dashboard</p>
                </div>
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="flex align-items-center gap-3 mb-3">
                <i className="pi pi-tags text-primary-500 text-xl"></i>
                <div>
                  <h4 className="mb-1">Organize Chats</h4>
                  <p className="text-600 m-0">Use tags to organize and track conversations</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: "none" }}>
        <QRToPrint ref={contentRef} shopName={shopName} chatUrl={chatUrl} />
      </div>
      <Menu ref={menu} popup model={shareItems} className="share-menu" />
      <Toast ref={toast} position="top-center" />
    </>
  );
};

export default QRPrintModal;
